// Merchant active orders — anything paid that hasn't been confirmed/cancelled.
//
// Until Phase 2 wires Stripe + the fulfillment state machine, this list
// will mostly be empty; included now so the menu item is real.

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Clock, Download, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { StatusPill } from '../../components/shared/primitives';
import { DataTable, AvatarCell, type Column } from '../../components/shared/DataTable';
import { DeliveryProof } from '../../components/shared/DeliveryProof';
import { Button } from '../../components/stripe-ui/Button';
import { MERCHANT_NAV } from '../_navs';
import { useMerchantNav } from '../../lib/pendingAction';
import { requests, receipts, delays, proofs, type ServiceRequestWithItems, type CertLang } from '../../lib/api';
import { formatHtgMinor } from '../../lib/money';
import { normaliseDeliveryCode, isCompleteDeliveryCode } from '../../lib/deliveryCode';

const ACTIVE_FULFILLMENT = ['paid', 'assigned_to_merchant', 'in_progress', 'ready_for_delivery', 'delivered', 'confirmed'];

// fulfillment.status -> the merchant's next-action button label.
const NEXT_ACTION: Record<string, string> = {
  not_started: 'Start preparing',
  preparing: 'Mark ready',
  ready: 'Out for delivery',
  out_for_delivery: 'Mark delivered',
};
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'not_started', label: 'Paid' },
  { key: 'preparing', label: 'Preparing' },
  { key: 'ready', label: 'Ready' },
  { key: 'out_for_delivery', label: 'Out for delivery' },
  { key: 'delivered', label: 'Awaiting code' },
];


export function MerchantOrdersPage() {
  const q = useQuery({ queryKey: ['merchant', 'orders'], queryFn: () => requests.list() });
  const orders = useMemo(() => (q.data ?? []).filter((r) => ACTIVE_FULFILLMENT.includes(r.status)), [q.data]);

  // Home carries a badge when this role owes an action, so it stays
  // visible from any screen.
  const nav = useMerchantNav(MERCHANT_NAV);

  const [filter, setFilter] = useState('all');

  const visible = useMemo(() => {
    if (filter === 'all') return orders;
    return orders.filter((r) => (r.fulfillment?.status ?? 'not_started') === filter);
  }, [orders, filter]);

  const columns: Column<ServiceRequestWithItems>[] = [
    {
      key: 'ref', header: 'Reference', type: 'mono', width: '110px',
      value: (r) => `REQ-${String(r.id).padStart(4, '0')}`,
    },
    {
      key: 'recipient', header: 'Recipient', type: 'node', width: 'minmax(0,1.3fr)',
      value: (r) => <AvatarCell name={r.recipient?.full_name ?? null} />,
    },
    {
      key: 'area', header: 'Area', type: 'text', width: '120px',
      value: (r) => r.city?.name ?? '—',
    },
    {
      key: 'items', header: 'Items', type: 'money', width: '70px',
      value: (r) => r.cart_item_count,
    },
    {
      key: 'goods', header: 'Goods', type: 'money', width: '120px',
      value: (r) => (r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor) : '—'),
    },
    {
      key: 'state', header: 'State', type: 'node', width: '150px',
      value: (r) => <StatusPill status={r.fulfillment?.status ?? 'not_started'} />,
    },
    {
      key: 'action', header: '', type: 'node', width: '200px',
      // A component, not inline JSX: the advance mutation and the proof query
      // are hooks, and a cell body runs inside the table's render.
      value: (r) => <OrderActions r={r} />,
    },
  ];

  return (
    <PortalShell
      nav={nav}
      activeId="orders"
      title="Active orders"
      subtitle="Paid orders to prepare and deliver."
    >
      <DataTable
        title="Active orders"
        subtitle="Paid; awaiting preparation, delivery, or recipient confirmation."
        filters={FILTERS}
        activeFilter={filter}
        onFilter={setFilter}
        columns={columns}
        rows={visible}
        rowKey={(r) => r.id}
        loading={q.isLoading}
        empty="No active orders. Once a customer pays a quote, the order shows up here."
        /* The hand-off photo belongs to the order but will not fit in a cell,
           and it is what gates "Mark delivered" — so it gets the expanded row
           rather than being dropped in the conversion. */
        renderExpanded={(r) => {
          const fs = r.fulfillment?.status ?? 'not_started';
          return ['out_for_delivery', 'delivered', 'confirmed'].includes(fs)
            ? <DeliveryProof requestId={r.id} canUpload />
            : null;
        }}
      />
    </PortalShell>
  );
}

function OrderActions({ r }: { r: ServiceRequestWithItems }) {
  const qc = useQueryClient();
  const fstatus = r.fulfillment?.status ?? 'not_started';
  const nextLabel = NEXT_ACTION[fstatus];
  const adv = useMutation({
    mutationFn: () => requests.advance(r.id),
    onSuccess: () => { toast.success('Order updated'); qc.invalidateQueries({ queryKey: ['merchant', 'orders'] }); },
    onError: (e: Error) => toast.error(e.message || 'Failed'),
  });

  // "Mark delivered" needs a hand-off photo first: it is what the recipient
  // judges before releasing payment, what the customer sees on their tracking,
  // and the evidence an admin weighs in a dispute. The server enforces this
  // too — this gate exists so the merchant sees the reason on the button
  // rather than getting a rejected request back.
  const proofQ = useQuery({
    queryKey: ['proofs', r.id],
    queryFn: () => proofs.list(r.id),
    enabled: fstatus === 'out_for_delivery',
  });
  const needsPhoto = fstatus === 'out_for_delivery' && (proofQ.data?.length ?? 0) === 0;

  return (
    <span className="flex flex-col items-end gap-1.5">
      {fstatus === 'confirmed'
        ? <ReceiptButton requestId={r.id} />
        : fstatus === 'delivered'
          ? <AtDoorConfirm requestId={r.id} />
          : nextLabel
            ? (
              <>
                <Button
                  size="sm"
                  onClick={() => adv.mutate()}
                  loading={adv.isPending}
                  disabled={adv.isPending || needsPhoto}
                  title={needsPhoto ? 'Add a hand-off photo first' : undefined}
                >{nextLabel}</Button>
                {needsPhoto && (
                  <span className="text-[11px] text-[var(--color-warning-fg)]">Add a hand-off photo first</span>
                )}
              </>
            )
            : <span className="inline-flex items-center gap-1 text-[11.5px] text-[var(--color-muted)]"><Clock className="w-3 h-3" /> awaiting confirmation</span>}

      {/* Report a delay while the order is still in flight. A stalled order
          the customer cannot explain is where support tickets and disputes
          come from. */}
      {!['delivered', 'confirmed'].includes(fstatus) && <ReportDelay requestId={r.id} />}
    </span>
  );
}

// Courier-OTP: at the door, the recipient reads out the code they received and
// the merchant enters it to confirm receipt. (The sender can never confirm.)
function AtDoorConfirm({ requestId }: { requestId: number }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const confirm = useMutation({
    mutationFn: () => requests.confirmReceipt(requestId, pin.trim()),
    onSuccess: () => { toast.success('Receipt confirmed with the recipient'); qc.invalidateQueries({ queryKey: ['merchant', 'orders'] }); },
    onError: (e: Error) => toast.error(e.message || "That code doesn't match"),
  });
  if (!open) return <Button size="sm" tone="outline" onClick={() => setOpen(true)}>Confirm at door</Button>;
  return (
    <div className="inline-flex items-center gap-1.5">
      <input
        value={pin} onChange={(e) => setPin(normaliseDeliveryCode(e.target.value))}
        inputMode="numeric" placeholder="code" autoFocus
        onKeyDown={(e) => { if (e.key === 'Enter' && isCompleteDeliveryCode(pin)) confirm.mutate(); }}
        className="w-24 px-2 py-1.5 text-[15px] font-mono tracking-[0.2em] text-center border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-success-border)]/40"
      />
      <Button size="sm" onClick={() => confirm.mutate()} loading={confirm.isPending} disabled={!isCompleteDeliveryCode(pin)}>Confirm</Button>
    </div>
  );
}

function ReceiptButton({ requestId }: { requestId: number }) {
  const [lang, setLang] = useState<CertLang>('en');
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try { await receipts.download(requestId, lang); }
    catch (e) { toast.error((e as Error).message || 'Download failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="inline-flex items-center gap-1.5">
      <select value={lang} onChange={(e) => setLang(e.target.value as CertLang)} className="text-[10.5px] border border-[var(--color-border)] rounded px-1 py-0.5 bg-white" aria-label="Receipt language">
        <option value="en">EN</option><option value="fr">FR</option><option value="ht">HT</option>
      </select>
      <button onClick={dl} disabled={busy} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--color-secondary)] hover:text-[var(--color-ink)]">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Receipt
      </button>
    </div>
  );
}

/**
 * Merchant control to say WHY an order is late.
 *
 * Deliberately a short list plus an optional note, not free text alone: a
 * structured reason can be translated, counted and eventually notified over
 * WhatsApp, while "stuck" cannot. The note carries the specifics.
 */
function ReportDelay({ requestId }: { requestId: number }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('road_blocked');
  const [note, setNote] = useState('');

  const report = useMutation({
    mutationFn: () => delays.report(requestId, { reason, note: note.trim() || undefined }),
    onSuccess: () => {
      toast.success('Delay reported — the customer can see it');
      setOpen(false);
      setNote('');
      qc.invalidateQueries({ queryKey: ['requests', requestId, 'delays'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Could not report the delay'),
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ml-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--color-warning-fg)] hover:underline"
      >
        <AlertTriangle className="w-3 h-3" /> Report delay
      </button>
    );
  }

  return (
    <div className="mt-2 text-left rounded-lg border border-[var(--color-warning-border)] bg-[var(--color-warning-bg)]/60 p-2.5">
      <select
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        aria-label="Delay reason"
        className="w-full rounded-md border border-[var(--color-border)] bg-white px-2 py-1.5 text-[12px]"
      >
        <option value="road_blocked">Road blocked</option>
        <option value="fuel_shortage">Fuel shortage</option>
        <option value="out_of_stock">Item out of stock</option>
        <option value="weather">Weather</option>
        <option value="other">Other</option>
      </select>
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={500}
        placeholder="Optional detail for the customer"
        className="mt-1.5 w-full rounded-md border border-[var(--color-border)] bg-white px-2 py-1.5 text-[12px]"
      />
      <div className="mt-2 flex items-center gap-2">
        <Button size="sm" onClick={() => report.mutate()} loading={report.isPending} disabled={report.isPending}>
          Send
        </Button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[11.5px] font-semibold text-[var(--color-secondary-2)] hover:underline"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
